import { CmsApiProgramList } from 'types';

import { ProgramType } from './enum';

export const AGE_RATINGS = [
  'SC',
  'AL',
  'L',
  'A6',
  '6',
  'A10',
  '10',
  'A12',
  '12',
  'A14',
  '14',
  'A16',
  '16',
  'A18',
  '18',
] as const;

export type AgeRatingKey = (typeof AGE_RATINGS)[number];

export type AgeRatingDefinition = {
  title: string;
  longTitle: string;
  description: string;
  badge: { label: string; color: string };
};

export const AgeRatingDefinitions: Record<AgeRatingKey, AgeRatingDefinition> = {
  SC: {
    title: 'Sem classificação',
    longTitle: 'Sem classificação',
    description: 'Sem classificação',
    badge: {
      color: 'transparent',
      label: 'SC',
    },
  },
  L: {
    title: 'Livre',
    longTitle: 'Livre para todos os públicos',
    description: 'Não expõe nenhuma faixa etária a nenhum conteúdo potencialmente prejudicial',
    badge: {
      color: '#00A651',
      label: 'L',
    },
  },
  AL: {
    title: 'Livre',
    longTitle: 'Autoclassificação Livre para todos os públicos',
    description: 'Não expõe nenhuma faixa etária a nenhum conteúdo potencialmente prejudicial',
    badge: {
      color: '#00A651',
      label: 'AL',
    },
  },
  '6': {
    title: '6 Anos',
    longTitle: 'Não recomendado para menores de 6 anos',
    description: 'Conteúdo com apresentação da tristeza de forma ponderada e/ou presença de armas',
    badge: {
      color: '#E43493',
      label: '6',
    },
  },
  A6: {
    title: '6 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 6 anos',
    description: 'Conteúdo com apresentação da tristeza de forma ponderada e/ou presença de armas',
    badge: {
      color: '#E43493',
      label: 'A6',
    },
  },
  '10': {
    title: '10 Anos',
    longTitle: 'Não recomendado para menores de 10 anos',
    description: 'Conteúdo violento ou linguagem inapropriada para crianças, em menor intensidade',
    badge: {
      color: '#0095DA',
      label: '10',
    },
  },
  A10: {
    title: '10 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 10 anos',
    description: 'Conteúdo violento ou linguagem inapropriada para crianças, em menor intensidade',
    badge: {
      color: '#0095DA',
      label: 'A10',
    },
  },
  '12': {
    title: '12 Anos',
    longTitle: 'Não recomendado para menores de 12 anos',
    description: 'As cenas podem conter agressão física, consumo de drogas e insinuação sexual',
    badge: {
      color: '#FBC115',
      label: '12',
    },
  },
  A12: {
    title: '12 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 12 anos',
    description: 'As cenas podem conter agressão física, consumo de drogas e insinuação sexual',
    badge: {
      color: '#FBC115',
      label: 'A12',
    },
  },
  '14': {
    title: '14 Anos',
    longTitle: 'Não recomendado para menores de 14 anos',
    description: 'Conteúdo mais violentos e/ou de linguagem sexual mais acentuada',
    badge: {
      color: '#F58220',
      label: '14',
    },
  },
  A14: {
    title: '14 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 14 anos',
    description: 'Conteúdo mais violentos e/ou de linguagem sexual mais acentuada',
    badge: {
      color: '#F58220',
      label: 'A14',
    },
  },
  '16': {
    title: '16 Anos',
    longTitle: 'Não recomendado para menores de 16 anos',
    description:
      'Conteúdo violento e/ou sexual intenso. Pode conter cenas de tortura, suicídio ou nudez total',
    badge: {
      color: '#D4080F',
      label: '16',
    },
  },
  A16: {
    title: '16 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 16 anos',
    description:
      'Conteúdo violento e/ou sexual intenso. Pode conter cenas de tortura, suicídio ou nudez total',
    badge: {
      color: '#D4080F',
      label: 'A16',
    },
  },
  '18': {
    title: '18 Anos',
    longTitle: 'Não recomendado para menores de 18 anos',
    description:
      'Conteúdo violento e/ou sexual extremo. Pode conter cenas de sexo explícito, mutilação ou abuso sexual',
    badge: {
      color: '#000000',
      label: '18',
    },
  },
  A18: {
    title: '18 Anos',
    longTitle: 'Autoclassificação não recomendado para menores de 18 anos',
    description:
      'Conteúdo violento e/ou sexual extremo. Pode conter cenas de sexo explícito, mutilação ou abuso sexual',
    badge: {
      color: '#000000',
      label: 'A18',
    },
  },
};

/**
 * Monta a string para filtrar por faixa etaria no CMS
 * @param key AgeRatingKey
 * @returns string
 */
export const getAgeRatingFilterString = (key: AgeRatingKey): string => {
  const items: string[] = AGE_RATINGS.slice(0, AGE_RATINGS.indexOf(key) + 1);
  // inclui programas sem rating definido
  if (key === '18') {
    items.push('null');
  }

  return items.map((item) => `rating=${item}`).join('&');
};

/**
 * Available age rating keys
 */
export const AVAILABLE_AGE_RATING_KEYS: AgeRatingKey[] = ['L', '6', '10', '12', '14', '16', '18'];

/**
 * Editable age ratings for profile (less than 18 or 'L')
 */
export const EDITABLE_AGE_RATINGS = AVAILABLE_AGE_RATING_KEYS.filter(
  (rating) => rating === 'L' || Number(rating) < 18
);

/**
 * Filters programs according to user's age rating.
 * Programs marked as 'isBet' are only visible if ageRating is 18.
 *
 * @param programs List of programs from CMS API
 * @param ageRating User's age rating
 * @returns Filtered list of programs
 */
export const filterProgramsForAge = (
  programs: CmsApiProgramList,
  ageRating: AgeRatingKey
): CmsApiProgramList => {
  return {
    ...programs,
    items: programs.items.filter((program) => {
      const isRestrictedBet = program?.isBet && ageRating !== '18';
      return !isRestrictedBet;
    }),
  };
};

/**
 * Filters programs according to the user's age rating and program type.
 * Programs marked as 'isBet' and LIVE or RADIO programs are only
 * visible if the ageRating is 18.
 *
 * @param programs List of programs from CMS API
 * @param ageRating User's age rating
 * @returns Filtered list of programs
 */
export const filterProgramsByAgeAndLiveType = (
  programs: CmsApiProgramList,
  ageRating: AgeRatingKey
): CmsApiProgramList => {
  return {
    ...programs,
    items: programs.items.filter((program) => {
      const isLive = program?.type === ProgramType.LIVE || program?.type === ProgramType.RADIO;

      const isBetAllowed = ageRating === '18';
      const isLiveAllowed = ageRating === '16' || ageRating === '18';

      const isRestrictedBet = program?.isBet && !isBetAllowed;
      const isRestrictedLive = isLive && !isLiveAllowed;

      return !isRestrictedBet && !isRestrictedLive;
    }),
  };
};
